import type { EmailAttribution } from "../analytics/emailAttribution";
import { config } from "../config";
import type { FlowVariant } from "../funnel/types";
import type { CheckoutProvider } from "../funnel/checkoutProvider";

type CreateCheckoutSessionRequest = {
  email?: string;
  leadId?: number;
  planId: string;
  flow: FlowVariant;
  provider?: CheckoutProvider;
  currency?: string;
  value?: number;
  eventId?: string;
  fbp?: string;
  fbc?: string;
  funnelSessionId?: string;
  emailAttribution?: EmailAttribution;
  paywallVariant?: string;
  paywallScenario?: string;
  paywallEntryPoint?: string;
  recoveryCode?: string;
};

export type CreateCheckoutSessionResponse = {
  checkoutUrl: string;
  sessionId: string;
  completionToken: string;
  planId: string;
  provider: CheckoutProvider;
  currency: string;
  value: number;
};

type RawCreateCheckoutSessionResponse = Omit<
  CreateCheckoutSessionResponse,
  "planId" | "provider" | "currency" | "value"
> & Partial<Pick<CreateCheckoutSessionResponse, "planId" | "provider" | "currency" | "value">>;

export type CheckoutSessionFailure = {
  code: string;
  status: number;
  message: string;
  retryable: boolean;
};

export class CheckoutSessionError extends Error {
  readonly code: string;
  readonly status: number;
  readonly retryable: boolean;

  constructor(failure: CheckoutSessionFailure) {
    super(failure.message);
    this.name = "CheckoutSessionError";
    this.code = failure.code;
    this.status = failure.status;
    this.retryable = failure.retryable;
  }
}

const SAFE_CHECKOUT_ERROR_CODES = ["provider_unavailable", "checkout_unavailable"] as const;

const checkoutFailureFromResponse = (status: number, code: unknown): CheckoutSessionFailure => {
  if (code === "invalid_email") {
    return {
      code: "invalid_email",
      status,
      message: "Please enter a valid email.",
      retryable: false
    };
  }
  if (status === 429) {
    return {
      code: "rate_limited",
      status,
      message: "Too many checkout attempts. Please wait a minute and try again.",
      retryable: true
    };
  }
  if (status === 410) {
    return {
      code: "checkout_link_expired",
      status,
      message: "This checkout link has expired. Please start checkout again.",
      retryable: false
    };
  }
  if (status === 409 && code === "already_paid") {
    return {
      code: "already_paid",
      status,
      message: "This checkout is already complete. Check your email for access details.",
      retryable: false
    };
  }
  if (status === 409 && code === "already_refunded") {
    return {
      code: "already_refunded",
      status,
      message: "This checkout was refunded. Please start a new checkout.",
      retryable: false
    };
  }

  return {
    code: typeof code === "string" && SAFE_CHECKOUT_ERROR_CODES.includes(
      code as (typeof SAFE_CHECKOUT_ERROR_CODES)[number]
    ) ? code : "checkout_unavailable",
    status,
    message: "Checkout is temporarily unavailable. Please try again shortly.",
    retryable: status >= 500
  };
};

export const getCheckoutSessionFailure = (error: unknown): CheckoutSessionFailure => {
  if (error instanceof CheckoutSessionError) {
    return {
      code: error.code,
      status: error.status,
      message: error.message,
      retryable: error.retryable
    };
  }

  return {
    code: "unexpected_checkout_error",
    status: 0,
    message: "Checkout is temporarily unavailable. Please try again shortly.",
    retryable: true
  };
};

export const resolveCheckoutEmailForAttempt = (
  value: unknown,
  hasEmailStep: boolean,
  priorErrorCode: string | null
): string => {
  if (!hasEmailStep && priorErrorCode !== "invalid_email") {
    return "";
  }
  return typeof value === "string" ? value.trim() : "";
};

const isCurrencyCode = (value: unknown): value is string =>
  typeof value === "string" && /^[A-Za-z]{3}$/.test(value.trim());

const isCheckoutProvider = (value: unknown): value is CheckoutProvider =>
  value === "polar" || value === "stripe";

export type StripePlanPrice = {
  priceId: string;
  productId: string;
  productName: string | null;
  unitAmount: number;
  currency: string;
  interval: string;
  intervalCount: number;
};

export type StripePricesResponse = {
  weekly?: StripePlanPrice;
  monthly: StripePlanPrice;
  quarterly: StripePlanPrice;
  annual: StripePlanPrice;
};

export const CHECKOUT_TIMEOUT_MS = 30_000;

export const createCheckoutSession = async (
  request: CreateCheckoutSessionRequest
): Promise<CreateCheckoutSessionResponse> => {
  if (!config.apiBaseUrl) {
    throw new Error("VITE_API_BASE_URL is not configured");
  }

  const controller = new AbortController();
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timeout = setTimeout(() => {
      controller.abort();
      reject(new CheckoutSessionError({
        code: "checkout_timeout",
        status: 0,
        message: "Checkout is taking longer than expected. Please try again.",
        retryable: true
      }));
    }, CHECKOUT_TIMEOUT_MS);
  });
  try {
    // Covers both the request and response body. Never automatically repeat a POST.
    return await Promise.race([deadline, (async () => {
      const response = await fetch(`${config.apiBaseUrl}/api/checkout/create-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
        signal: controller.signal
      });

      if (!response.ok) {
        let code: unknown;
        try {
          const errBody = (await response.json()) as { code?: unknown };
          code = errBody?.code;
        } catch {
          // ignore non-JSON error bodies
        }
        throw new CheckoutSessionError(checkoutFailureFromResponse(response.status, code));
      }

      const payload = await response.json() as RawCreateCheckoutSessionResponse;
      let validUrl = false;
      try {
        const url = new URL(payload?.checkoutUrl);
        validUrl = url.protocol === "https:" && !url.username && !url.password;
      } catch { /* Invalid responses must never become navigation targets. */ }
      if (!validUrl || typeof payload?.sessionId !== "string" || !payload.sessionId.trim() ||
          typeof payload?.completionToken !== "string" || !payload.completionToken.trim()) {
        throw new CheckoutSessionError(checkoutFailureFromResponse(502, "checkout_unavailable"));
      }
      const fallbackCurrency = isCurrencyCode(request.currency) ? request.currency.trim().toUpperCase() : "USD";
      const fallbackValue =
        typeof request.value === "number" && Number.isFinite(request.value) && request.value >= 0
          ? request.value
          : 0;

      return {
        ...payload,
        planId: typeof payload.planId === "string" && payload.planId.trim() ? payload.planId : request.planId,
        provider: isCheckoutProvider(payload.provider) ? payload.provider : request.provider ?? "polar",
        currency: isCurrencyCode(payload.currency) ? payload.currency.trim().toUpperCase() : fallbackCurrency,
        value:
          typeof payload.value === "number" && Number.isFinite(payload.value) && payload.value >= 0
            ? payload.value
            : fallbackValue
      };
    })()]);
  } finally {
    clearTimeout(timeout);
  }
};

export const fetchPrices = async (
  country?: string,
  provider?: CheckoutProvider,
  paywallVariant?: string,
  currency?: string
): Promise<StripePricesResponse> => {
  if (!config.apiBaseUrl) {
    throw new Error("VITE_API_BASE_URL is not configured");
  }

  const url = new URL(`${config.apiBaseUrl}/api/checkout/prices`);
  if (country) {
    url.searchParams.set("country", country);
  }
  if (provider) {
    url.searchParams.set("checkout", provider);
  }
  if (paywallVariant) {
    url.searchParams.set("paywall_variant", paywallVariant);
  }
  if (currency) {
    url.searchParams.set("currency", currency);
  }

  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error(`Failed to fetch prices: ${response.status}`);
  }

  return response.json() as Promise<StripePricesResponse>;
};
